import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MaskLines } from "./Reveal";
import Button from "./Button";
import { HERO_VIDEO, HERO_POSTER } from "../lib/data";

export default function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const reduce = useReducedMotion();
  const [videoFailed, setVideoFailed] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  // Set muted/inline SYNCHRONOUSLY as the node mounts (before iOS evaluates autoplay)
  const setVideoNode = (el) => {
    videoRef.current = el;
    if (el) {
      el.muted = true;
      el.defaultMuted = true;
      el.setAttribute("muted", "");
      el.setAttribute("playsinline", "");
      el.setAttribute("webkit-playsinline", "");
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => {
      v.muted = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    tryPlay();
    const onPlaying = () => setVideoFailed(false);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("canplay", tryPlay);
    v.addEventListener("loadeddata", tryPlay);
    const onTouch = () => tryPlay();
    window.addEventListener("touchstart", onTouch, { passive: true });
    const onVis = () => document.visibilityState === "visible" && tryPlay();
    document.addEventListener("visibilitychange", onVis);
    // If autoplay is truly blocked (e.g. iOS Low Power Mode), fall back to the still.
    const t = setTimeout(() => {
      if (v.paused || v.currentTime === 0) setVideoFailed(true);
    }, 2200);
    return () => {
      clearTimeout(t);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("canplay", tryPlay);
      v.removeEventListener("loadeddata", tryPlay);
      window.removeEventListener("touchstart", onTouch);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] w-full overflow-hidden" data-testid="hero">
      <motion.div className="absolute inset-0" style={reduce ? {} : { y, scale }}>
        <img
          src={HERO_POSTER}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <video
          ref={setVideoNode}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
            videoFailed ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_POSTER}
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
          <source src="/hero.webm" type="video/webm" />
        </video>
      </motion.div>
      <motion.div className="absolute inset-0 bg-ink" style={{ opacity: overlay }} />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-ink/40" />

      <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 sm:px-10 flex flex-col justify-end pb-20 sm:pb-28">
        <motion.div
          className="label mb-6 flex items-center gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 0.8 }}
        >
          <span className="inline-block w-8 h-px bg-silver" />
          NSTR Talent
        </motion.div>

        <h1 className="font-sans font-semibold text-warm uppercase leading-[0.9] sm:leading-[0.86] tracking-[-0.02em] text-[2.7rem] sm:text-8xl lg:text-[9rem]">
          <MaskLines lines={["Sport. Culture.", "Personality."]} startDelay={1.85} wordStagger={0.06} />
        </h1>

        <motion.p
          className="mt-8 max-w-xl text-silver text-base sm:text-lg leading-relaxed font-light"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5, duration: 0.9 }}
        >
          Representing the next generation of athletes, creators and entertainers.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.7, duration: 0.9 }}
        >
          <Button as="a" href="#brand-inquiry" variant="primary" className="w-full sm:w-auto" data-testid="hero-work">
            Work With NSTR
          </Button>
          <Button as="a" href="#apply" variant="outline" className="w-full sm:w-auto" data-testid="hero-join">
            Join The Roster
          </Button>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-8 right-6 sm:right-10 z-10 label hidden sm:flex items-center gap-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3, duration: 1 }}
      >
        Scroll
        <span className="inline-block w-px h-10 bg-silver/50 overflow-hidden relative">
          <motion.span
            className="absolute inset-0 bg-warm"
            animate={{ y: ["-100%", "100%"] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
}
